/* mo-bd-pocket.js: "Will it stay on?" for the sheets page (sections/mo-bd-main.liquid), Mattress Overstock 2026-10-14.
   One corner of a mattress in a 3/4 view, drawn at the height the shopper picks, with a fitted sheet on it.
   The sheet is a Verlet cloth (after 21st.dev Cloth Simulation 29269 and Woven Cloth 25571, rebuilt in plain
   canvas 2D). Its flat pattern is the real shape of a fitted corner: a square of top fabric with two skirts the
   length of the pocket plus a tuck, sewn together down the corner, an elastic hem along the skirt edges.
   - Pocket deep enough for the mattress: the hem is pulled under the bottom edge and holds.
   - Pocket too shallow: the hem tries to tuck, cannot hold, springs off, rides up over the top edge and the
     sheet slides back off the corner, then settles.
   The fit limit is the maker's published figure (Purple: up to 15.5 in.; Tempur-Pedic: 13 in. pocket).
   It shows the idea, not a measurement. Units are inches. Classic script, no dependencies.
   window.MOBdPocket.mount(canvas, opts) returns { setHeight, setSheet, setColor, setDark, settle, wake }. */
(function () {
  'use strict';

  var TUCK = 3;        // inches of skirt that must reach under the bottom edge to hold
  var A = 40;          // top fabric simulated from the corner back, inches (the whole drawn corner)
  var H_STEP = 2;      // mesh spacing on the top, inches
  var BED = 40;        // drawn mattress extent from the corner, inches
  var BASE = 5.5;      // foundation height under the mattress, inches
  var R_TOP = 0.28, R_SKIRT = 0.5; // cloth thickness off a surface (skirt rides over top cloth when folded)
  var AZ = 38 * Math.PI / 180, EL = 30 * Math.PI / 180;
  var SA = Math.sin(AZ), CA = Math.cos(AZ), SE = Math.sin(EL), CE = Math.cos(EL);
  // camera basis: right, up, toward camera
  var RX = CA, RZ = -SA;
  var UX = -SA * SE, UY = CE, UZ = -CA * SE;
  var DX = SA * CE, DY = SE, DZ = CA * CE;
  var PERSP = 260;
  var LIGHT = norm([0.35, 0.9, 0.55]);

  function norm(v) { var l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; }
  function hex(c) { c = (c || '#ffffff').replace('#', ''); if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2]; return [parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)]; }
  function rgb(a) { return 'rgb(' + (a[0] | 0) + ',' + (a[1] | 0) + ',' + (a[2] | 0) + ')'; }
  function mix(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]; }
  function shadeCol(base, nx, ny, nz, back) {
    var lit = 0.62 + 0.55 * Math.max(0, nx * LIGHT[0] + ny * LIGHT[1] + nz * LIGHT[2]);
    var k = tone(lit) / tone(1.15);
    var col = mix(mix(base, [0, 0, 0], 0.5), base, Math.min(1.1, k));
    if (back) col = mix(col, [0, 0, 0], 0.08);
    return rgb(col.map(function (x) { return Math.min(255, x); }));
  }
  function tone(x) { x = Math.max(0, x); return (x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14); } // filmic curve keeps white folds readable

  /* ---------------- cloth: flat pattern of one fitted corner ---------------- */
  function buildCloth(S) {
    var NA = Math.round(A / H_STEP), ht = A / NA, NS = Math.max(2, Math.round(S / 1.45)), hs = S / NS;
    var N = NA + NS;
    var idx = new Int32Array((N + 1) * (N + 1)).fill(-1);
    var fx = [], fz = [], kind = []; // kind 0 top, 1 right skirt (x face), 2 front skirt (z face), 3 corner seam
    // finer near the corner where the cloth folds, coarser far back where it lies flat
    function flat(i) { return i <= NA ? -A * Math.pow((NA - i) / NA, 1.35) : (i - NA) * hs; }
    var n = 0;
    for (var j = 0; j <= N; j++) for (var i = 0; i <= N; i++) {
      if (i > NA && j > NA) continue;
      if (i === NA && j > NA) continue; // merged into (NA + k, NA)
      idx[j * (N + 1) + i] = n++;
      fx.push(flat(i)); fz.push(flat(j));
      kind.push(i <= NA && j <= NA ? 0 : (j === NA && i > NA ? 3 : (i > NA ? 1 : 2)));
    }
    for (var k = 1; k <= NS; k++) idx[(NA + k) * (N + 1) + NA] = idx[NA * (N + 1) + NA + k];
    function at(i, j) { return (i < 0 || j < 0 || i > N || j > N) ? -1 : idx[j * (N + 1) + i]; }
    // the distance two pattern points keep is their distance on the flat pattern, measured the way the
    // fabric is cut: across the seam the two skirts meet edge to edge
    function pos2(i, j) { return [flat(i), flat(j)]; }
    var seen = {}, ca = [], cb = [], cr = [], cs = [], cn = [];
    function cdist(i, j) { var x = flat(i), z = flat(j); return x <= 0 && z <= 0 ? Math.hypot(x, z) : (x > 0 ? -Math.min(0, z) : -Math.min(0, x)); }
    function link(i1, j1, i2, j2, stiff) {
      var a = at(i1, j1), b = at(i2, j2);
      if (a < 0 || b < 0 || a === b) return;
      var key = a < b ? a + '_' + b : b + '_' + a;
      if (seen[key]) return; seen[key] = 1;
      var p = pos2(i1, j1), q = pos2(i2, j2);
      var r = Math.hypot(p[0] - q[0], p[1] - q[1]);
      // links that cross the corner seam (one end on each skirt) measure along the sewn edge
      var ka = kind[a], kb = kind[b], da = -1, db = -2;
      if ((ka === 1 && kb === 2) || (ka === 2 && kb === 1)) return;
      // skirt fabric runs along the side gathered by the elastic: more cloth than hem, more of it near the hem
      if (ka && kb) {
        var da = Math.max(flat(i1) > 0 ? flat(i1) : 0, flat(j1) > 0 ? flat(j1) : 0), db = Math.max(flat(i2) > 0 ? flat(i2) : 0, flat(j2) > 0 ? flat(j2) : 0);
        if (Math.abs(da - db) < 1e-6) r *= 1 + 0.32 * (da / S) * Math.max(0, 1 - Math.min(cdist(i1, j1), cdist(i2, j2)) / 20);
      }
      var hemLink = da === S && db === S && ka && kb;
      var near = Math.min(cdist(i1, j1), cdist(i2, j2)) < 14;
      ca.push(a); cb.push(b); cr.push(hemLink ? r * 0.8 : r); cs.push(hemLink ? 0.35 : stiff); cn.push(near ? 1 : 0);
    }
    for (j = 0; j <= N; j++) for (i = 0; i <= N; i++) {
      if (at(i, j) < 0) continue;
      link(i, j, i + 1, j, 1); link(i, j, i, j + 1, 1);
      link(i, j, i + 1, j + 1, 0.7); link(i + 1, j, i, j + 1, 0.7);
      link(i, j, i + 2, j, 0.08); link(i, j, i, j + 2, 0.08);
    }
    // triangles (two per cell that exists)
    var tri = [];
    for (j = 0; j < N; j++) for (i = 0; i < N; i++) {
      if (i >= NA && j >= NA) continue;
      var a1 = at(i, j), b1 = at(i + 1, j), c1 = at(i, j + 1), d1 = at(i + 1, j + 1);
      if (a1 < 0 || b1 < 0 || c1 < 0 || d1 < 0) continue;
      tri.push(a1, b1, d1, a1, d1, c1);
    }
    // hem: skirt edge rows, ordered along the edge, joined at the corner
    var hem = [];
    for (j = 0; j <= NA; j++) hem.push(at(N, j));        // right skirt hem, back to front
    for (i = NA - 1; i >= 0; i--) hem.push(at(i, N));     // front skirt hem, corner to far end
    var isHem = new Uint8Array(n); hem.forEach(function (h) { if (h >= 0) isHem[h] = 1; });
    var depth = new Float32Array(n); // distance down the skirt from the top edge (0 on the top)
    for (j = 0; j <= N; j++) for (i = 0; i <= N; i++) { var q2 = at(i, j); if (q2 >= 0) depth[q2] = Math.max(0, fx[q2] > 0 ? fx[q2] : 0, fz[q2] > 0 ? fz[q2] : 0); }
    return { n: n, NA: NA, NS: NS, S: S, fx: fx, fz: fz, kind: kind, ca: ca, cb: cb, cr: cr, cs: cs, cn: cn, tri: tri, hem: hem.filter(function (h) { return h >= 0; }), isHem: isHem, depth: depth };
  }

  // where a pattern point sits on a mattress of height H. dressed: hem tucked under; else hanging straight down
  // lift: the last inches of skirt still outside, hanging below the edge (the hem about to snap under)
  function poseOf(c, p, H, dressed, out, lift) {
    var d = c.depth[p], k = c.kind[p], fx = c.fx[p], fz = c.fz[p];
    if (k === 0) { out[0] = fx; out[1] = H + R_TOP; out[2] = fz; return; }
    var under = dressed ? d - H : 0;
    if (dressed && lift) under = d - H <= lift ? 0 : d - H - lift;
    var y = dressed && lift && d - H > 0 ? Math.max(H - d, -lift) : H - d, r = R_SKIRT;
    if (k === 1) { if (under > 0) { out[0] = -under; out[1] = -0.3; out[2] = Math.min(fz, -0.4); } else { out[0] = r; out[1] = y; out[2] = fz; } return; }
    if (k === 2) { if (under > 0) { out[0] = Math.min(fx, -0.4); out[1] = -0.3; out[2] = -under; } else { out[0] = fx; out[1] = y; out[2] = r; } return; }
    if (under > 0) { out[0] = -under * 0.72; out[1] = -0.3; out[2] = -under * 0.72; } else { out[0] = r; out[1] = y; out[2] = r; }
  }

  /* ---------------- simulation ---------------- */
  function Sim(sheet, H) {
    this.sheet = sheet; this.H = H; this.Ht = H;
    this.c = buildCloth(sheet.pocket + TUCK);
    var n = this.c.n;
    this.p = new Float32Array(n * 3); this.o = new Float32Array(n * 3); this.home = new Float32Array(n * 3);
    this.t = 0; this.mode = 'fit'; this.failT = 0; this.slide = 0; this.calm = 0; this.grab = -1; this.grabTo = null;
    this.dress(true, 0);
  }
  Sim.prototype.fits = function (H) { return H <= this.sheet.pocket + 1e-6; };
  // put the sheet on: dressed=true starts tucked; otherwise the skirts hang straight down and the hem has to work.
  // lift: start the hem this many inches short of its tuck (the small snap on first view)
  Sim.prototype.dress = function (dressed, lift) {
    var c = this.c, v = [0, 0, 0], H = this.H;
    for (var i = 0; i < c.n; i++) {
      poseOf(c, i, H, dressed, v, lift);
      this.p[i * 3] = v[0]; this.p[i * 3 + 1] = v[1]; this.p[i * 3 + 2] = v[2];
      this.o[i * 3] = v[0]; this.o[i * 3 + 1] = v[1]; this.o[i * 3 + 2] = v[2];
    }
    this.mode = this.fits(H) ? 'fit' : 'fail'; this.failT = 0; this.slide = 0; this.calm = 0; this.t = 0;
  };
  Sim.prototype.collide = function (i) {
    var P = this.p, H = this.H, b = i * 3, x = P[b], y = P[b + 1], z = P[b + 2];
    var r = this.c.kind[i] === 0 ? R_TOP : R_SKIRT;
    // mattress: x in [-BED, 0], y in [0, H], z in [-BED, 0]; foundation below with a thin gap the tuck sits in
    if (x < r && z < r && x > -BED - 4 && z > -BED - 4) {
      if (y > -0.05 && y < H + r) {
        var dx = r - x, dz = r - z, dy = H + r - y, db = y + 0.3;
        var m = Math.min(dx, dz, dy, db);
        if (m === dy) P[b + 1] = H + r; else if (m === dx) P[b] = r; else if (m === dz) P[b + 2] = r; else P[b + 1] = -0.3;
      } else if (y <= -0.05 && y > -0.6) {
        if (x < 0 && z < 0) P[b + 1] = -0.3; // in the gap under the mattress
      } else if (y <= -0.6 && y > -BASE - 1) {
        var ex = r - x, ez = r - z, eu = -0.3 - y;
        var m2 = Math.min(ex, ez, eu);
        if (m2 === ex) P[b] = r; else if (m2 === ez) P[b + 2] = r; else P[b + 1] = -0.3;
      }
    }
    if (P[b + 1] < -BASE - 0.5) P[b + 1] = -BASE - 0.5; // floor
  };
  Sim.prototype.step = function (dt) {
    var c = this.c, P = this.p, O = this.o, n = c.n, H;
    // the mattress grows or shrinks toward the chosen height under the sheet
    var dH = this.Ht - this.H;
    if (Math.abs(dH) > 1e-3) { this.H += Math.sign(dH) * Math.min(Math.abs(dH), 14 * dt); this.calm = 0; }
    H = this.H;
    this.t += dt;
    var fits = this.fits(H);
    if (this.mode === 'fit' && !fits) { this.mode = 'fail'; this.failT = 0; }
    if (this.mode === 'fail') this.failT += dt;
    var popping = this.mode === 'fail' && this.failT > 0.45; // it holds for a moment, then springs off
    if (popping) this.slide = Math.min(2.5, this.slide + dt * 3);
    var damp = this.calm > 1.2 ? 0.9 : 0.985;
    var g = -260, dt2 = dt * dt;
    for (var i = 0; i < n; i++) {
      var b = i * 3, x = P[b], y = P[b + 1], z = P[b + 2];
      var ax = 0, ay = g, az = 0;
      // cloth lying over the top settles flat instead of tenting
      if (x < -0.3 && z < -0.3 && y > H + 0.9) ay -= 900;
      if (popping && !c.isHem[i]) {
        // the rest of the sheet pulls this corner back toward the middle of the bed: the top slides away from
        // the corner and drags the short skirt up the side and over the edge
        var kk = c.kind[i], fx0 = c.fx[i], fz0 = c.fz[i];
        var dist = kk === 0 ? Math.hypot(Math.min(0, fx0), Math.min(0, fz0)) : (kk === 1 ? -fz0 : (kk === 2 ? -fx0 : 0));
        var wc = Math.max(0, 1 - dist / 14);
        if (kk === 0) { if (fx0 - x + fz0 - z < 11 * wc) { ax -= 520 * wc; az -= 520 * wc; } }
        else if (!(x < -0.1 && z < -0.1)) { ay += (420 + 55 * c.depth[i]) * wc; }
        
      }
      if (c.isHem[i]) {
        var k = c.kind[i];
        var nx = (k === 1 || k === 3) ? 1 : 0, nz = (k === 2 || k === 3) ? 1 : 0; // outward of the face the hem hangs on
        // near the corner the band lets go first; far along the side it still hangs
        var far = k === 1 ? -c.fz[i] : (k === 2 ? -c.fx[i] : 0), w = Math.max(0, 1 - far / 14);
        var inside = x < -0.1 && z < -0.1; // over the mattress footprint (on top or under it)
        if (!popping) {
          // the elastic pulls the hem down the side, then in under the bottom edge
          if (!inside && y > 0.1) { ay -= 520; ax -= nx * 120; az -= nz * 120; }
          else if (!inside && y < -0.45) { ay += 1400; ax -= nx * 200; az -= nz * 200; }
          else {
            // under the edge: pull in only as far as the skirt reaches (its length past the mattress height)
            var under = k === 1 ? -x : (k === 2 ? -z : Math.min(-x, -z) * 1.4);
            if (under < c.S - H - 0.4) { ax -= nx * 1300; az -= nz * 1300; }
            ay -= y > -0.3 ? 200 : 0;
          }
        } else {
          // nothing under the edge to hold it: the band shortens the other way, up the side and back over the top
          if (inside && y < H * 0.5) { ax += nx * 900 * w; az += nz * 900 * w; }
          else if (!inside) { ay += 1600 * w; ax -= nx * 60 * w; az -= nz * 60 * w; }
          else if ((nx && x > -2.5) || (nz && z > -2.5)) { ax -= nx * 500 * w; az -= nz * 500 * w; } // over the edge, then it lies there
        }
      }
      var vx = (x - O[b]) * damp, vy = (y - O[b + 1]) * damp, vz = (z - O[b + 2]) * damp;
      O[b] = x; O[b + 1] = y; O[b + 2] = z;
      P[b] = x + vx + ax * dt2; P[b + 1] = y + vy + ay * dt2; P[b + 2] = z + vz + az * dt2;
    }
    // pins: the far edges of the top belong to the rest of the sheet; when the corner lets go they slide back
    var s = this.slide, pv = this._pv || (this._pv = [0, 0, 0]);
    for (i = 0; i < n; i++) {
      if (c.fx[i] <= -A + 1e-6 || c.fz[i] <= -A + 1e-6) {
        var bb = i * 3;
        poseOf(c, i, H, fits, pv);
        if (c.kind[i] === 1) pv[2] -= s; else if (c.kind[i] === 2) pv[0] -= s; else { pv[0] -= s; pv[2] -= s; }
        if (!fits && pv[1] < -BASE + 0.4) pv[1] = -BASE + 0.4;
        P[bb] = pv[0]; P[bb + 1] = pv[1]; P[bb + 2] = pv[2];
      }
    }
    if (this.grab >= 0 && this.grabTo) { var gb = this.grab * 3; P[gb] += (this.grabTo[0] - P[gb]) * 0.5; P[gb + 1] += (this.grabTo[1] - P[gb + 1]) * 0.5; P[gb + 2] += (this.grabTo[2] - P[gb + 2]) * 0.5; }
    var ca = c.ca, cb = c.cb, cr = c.cr, cs = c.cs, cn = c.cn, m = ca.length;
    var hemShort = 0.9; // the elastic hem is sewn gathered
    var soft = popping ? 0.12 : 0.6;
    for (var it = 0; it < 9; it++) {
      for (var e = 0; e < m; e++) {
        var a3 = ca[e] * 3, b3 = cb[e] * 3;
        var ddx = P[b3] - P[a3], ddy = P[b3 + 1] - P[a3 + 1], ddz = P[b3 + 2] - P[a3 + 2];
        var d = Math.sqrt(ddx * ddx + ddy * ddy + ddz * ddz) || 1e-6;
        var rest = cr[e];
        var st = cs[e];
        if (d < rest) { if (st < 0.5) continue; st *= (cn[e] || (c.kind[ca[e]] === 0 && c.kind[cb[e]] === 0)) ? soft : 0.6; } // cloth resists stretch; it gives (gathers) under compression
        var f = (d - rest) / d * 0.5 * st;
        P[a3] += ddx * f; P[a3 + 1] += ddy * f; P[a3 + 2] += ddz * f;
        P[b3] -= ddx * f; P[b3 + 1] -= ddy * f; P[b3 + 2] -= ddz * f;
      }
      for (i = 0; i < n; i++) this.collide(i);
    }
    // calm: how much the cloth still moves
    var mv = 0;
    for (i = 0; i < n * 3; i++) { var dd = Math.abs(P[i] - O[i]); if (dd > mv) mv = dd; }
    if (mv < 0.004 && (this.mode === 'fit' || this.failT > 1.6)) this.calm += dt; else this.calm = Math.max(0, this.calm - dt * 0.5);
    return mv;
  };
  Sim.prototype.asleep = function () { return this.calm > 2.4 && this.grab < 0 && Math.abs(this.Ht - this.H) < 1e-3; };

  /* ---------------- drawing ---------------- */
  function project(x, y, z, view) {
    var sx = x * RX + z * RZ, sy = -(x * UX + y * UY + z * UZ), dz = x * DX + y * DY + z * DZ;
    var f = PERSP / (PERSP - dz);
    return [view.cx + sx * f * view.s, view.cy + sy * f * view.s, dz];
  }

  function Painter(canvas) { this.cv = canvas; this.ctx = canvas.getContext('2d'); this.dark = false; this.bg = '#f5f3f2'; }
  Painter.prototype.palette = function () {
    var d = this.dark;
    return {
      bg: hex(this.bg),
      top: d ? hex('#8d877e') : hex('#d8d1c5'),
      side: d ? hex('#7a746b') : hex('#cbc3b6'),
      quilt: d ? 'rgba(30,26,22,0.22)' : 'rgba(110,96,80,0.22)',
      piping: d ? '#625c54' : '#b3aa9b',
      base: d ? hex('#4a4540') : hex('#5d5853'),
      shadow: d ? 'rgba(0,0,0,0.45)' : 'rgba(60,40,20,0.16)',
      red: d ? '#ff5a51' : '#bc000c',
      ink: d ? '#ece7df' : '#1b1c1d'
    };
  };
  Painter.prototype.view = function (W, Hc) {
    // fixed framing for the 5:4 stage: the corner sits right of center and the bed runs off the frame,
    // so it reads as one corner of a bed; an 18 in. bed still fits
    // the frame is always 5:4 from the top; a shallower stage crops its empty floor (the still does the same)
    var Fh = Math.max(Hc, W * 0.8);
    var s = Math.min(W / 44, Fh / 37);
    return { s: s, cx: W * 0.6, cy: Fh * 0.63 };
  };
  Painter.prototype.quad = function (pts, fill) {
    var c = this.ctx; c.beginPath(); c.moveTo(pts[0][0], pts[0][1]);
    for (var i = 1; i < pts.length; i++) c.lineTo(pts[i][0], pts[i][1]);
    c.closePath(); c.fillStyle = fill; c.fill();
  };
  Painter.prototype.draw = function (sim, labels) {
    var cv = this.cv, ctx = this.ctx, W = cv.width, Hc = cv.height, pal = this.palette(), v = this.view(W, Hc);
    var H = sim.H, P = sim.p, c = sim.c, i, a, b, q;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = rgb(pal.bg); ctx.fillRect(0, 0, W, Hc);
    function pr(x, y, z) { return project(x, y, z, v); }
    // floor shadow
    var sh = [pr(4, -BASE, 5), pr(-BED, -BASE, 5), pr(-BED, -BASE, -BED), pr(5, -BASE, -BED)];
    ctx.save(); ctx.filter = 'blur(' + Math.round(v.s * 1.6) + 'px)'; this.quad(sh, pal.shadow); ctx.restore();
    // foundation
    var fb = [[0, -BASE, 0], [-BED, -BASE, 0], [-BED, -0.6, 0], [0, -0.6, 0]].map(function (p) { return pr(p[0], p[1], p[2]); });
    var fs = [[0, -BASE, 0], [0, -BASE, -BED], [0, -0.6, -BED], [0, -0.6, 0]].map(function (p) { return pr(p[0], p[1], p[2]); });
    this.quad(fb, rgb(mix(pal.base, [0, 0, 0], 0.05))); this.quad(fs, rgb(mix(pal.base, [0, 0, 0], 0.22)));
    // cloth triangles: split hidden (tucked under) and visible, sort visible far to near
    var n = c.n, scr = new Array(n);
    for (i = 0; i < n; i++) scr[i] = pr(P[i * 3], P[i * 3 + 1], P[i * 3 + 2]);
    var T = c.tri, list = [], VN = sim._vn || (sim._vn = new Float32Array(n * 3));
    VN.fill(0);
    for (i = 0; i < T.length; i += 3) {
      a = T[i]; b = T[i + 1]; q = T[i + 2];
      var ux0 = P[b * 3] - P[a * 3], uy0 = P[b * 3 + 1] - P[a * 3 + 1], uz0 = P[b * 3 + 2] - P[a * 3 + 2];
      var wx0 = P[q * 3] - P[a * 3], wy0 = P[q * 3 + 1] - P[a * 3 + 1], wz0 = P[q * 3 + 2] - P[a * 3 + 2];
      var fnx = wy0 * uz0 - wz0 * uy0, fny = wz0 * ux0 - wx0 * uz0, fnz = wx0 * uy0 - wy0 * ux0;
      [a, b, q].forEach(function (k) { VN[k * 3] += fnx; VN[k * 3 + 1] += fny; VN[k * 3 + 2] += fnz; });
    }
    for (i = 0; i < T.length; i += 3) {
      a = T[i]; b = T[i + 1]; q = T[i + 2];
      var cy = (P[a * 3 + 1] + P[b * 3 + 1] + P[q * 3 + 1]) / 3, cx = (P[a * 3] + P[b * 3] + P[q * 3]) / 3, cz = (P[a * 3 + 2] + P[b * 3 + 2] + P[q * 3 + 2]) / 3;
      if (cy < -0.05 && cx < 0.05 && cz < 0.05) continue; // tucked under the mattress: hidden
      list.push([i, (scr[a][2] + scr[b][2] + scr[q][2]) / 3]);
    }
    list.sort(function (m, k) { return m[1] - k[1]; });
    // mattress faces
    var mf = [[0, 0, 0], [-BED, 0, 0], [-BED, H, 0], [0, H, 0]].map(function (p) { return pr(p[0], p[1], p[2]); });
    var ms = [[0, 0, 0], [0, 0, -BED], [0, H, -BED], [0, H, 0]].map(function (p) { return pr(p[0], p[1], p[2]); });
    var mt = [[0, H, 0], [-BED, H, 0], [-BED, H, -BED], [0, H, -BED]].map(function (p) { return pr(p[0], p[1], p[2]); });
    var L = LIGHT;
    this.quad(mf, rgb(pal.side.map(function (x) { return x * (0.62 + 0.4 * L[2]); })));
    this.quad(ms, rgb(pal.side.map(function (x) { return x * (0.6 + 0.4 * L[0]); })));
    this.quad(mt, rgb(pal.top));
    // quilting on the top, piping on the edges (a mattress, not a box)
    ctx.save(); ctx.beginPath(); ctx.moveTo(mt[0][0], mt[0][1]); for (i = 1; i < 4; i++) ctx.lineTo(mt[i][0], mt[i][1]); ctx.closePath(); ctx.clip();
    ctx.strokeStyle = pal.quilt; ctx.lineWidth = Math.max(1, v.s * 0.08);
    for (var u = -BED * 2; u <= BED; u += 6) {
      var p1 = pr(u, H, 0), p2 = pr(u + BED, H, -BED), p3 = pr(u, H, -BED), p4 = pr(u + BED, H, 0);
      ctx.beginPath(); ctx.moveTo(p1[0], p1[1]); ctx.lineTo(p2[0], p2[1]); ctx.moveTo(p3[0], p3[1]); ctx.lineTo(p4[0], p4[1]); ctx.stroke();
    }
    ctx.restore();
    ctx.strokeStyle = pal.piping; ctx.lineWidth = Math.max(1, v.s * 0.16);
    [[[-BED, H, 0], [0, H, 0], [0, H, -BED]], [[-BED, 0.15, 0], [0, 0.15, 0], [0, 0.15, -BED]]].forEach(function (line) {
      ctx.beginPath(); line.forEach(function (p, k) { var s2 = pr(p[0], p[1], p[2]); if (k) ctx.lineTo(s2[0], s2[1]); else ctx.moveTo(s2[0], s2[1]); }); ctx.stroke();
    });
    // cloth. While it holds, everything past the bottom edge is tucked: clip the drawing at the edge
    ctx.save();
    if (sim.mode === 'fit') {
      var k1 = pr(-BED, 0, R_SKIRT + 0.2), k2 = pr(R_SKIRT + 0.2, 0, R_SKIRT + 0.2), k3 = pr(R_SKIRT + 0.2, 0, -BED);
      ctx.beginPath(); ctx.moveTo(-10, k1[1]); ctx.lineTo(k1[0], k1[1]); ctx.lineTo(k2[0], k2[1]); ctx.lineTo(k3[0], k3[1]); ctx.lineTo(W + 10, k3[1]); ctx.lineTo(W + 10, -10); ctx.lineTo(-10, -10); ctx.closePath(); ctx.clip();
    }
    var base = hex(sim.sheet.color);
    var vdir = [DX, DY, DZ];
    for (var t = 0; t < list.length; t++) {
      i = list[t][0]; a = T[i]; b = T[i + 1]; q = T[i + 2];
      var nx = VN[a * 3] + VN[b * 3] + VN[q * 3], ny = VN[a * 3 + 1] + VN[b * 3 + 1] + VN[q * 3 + 1], nz = VN[a * 3 + 2] + VN[b * 3 + 2] + VN[q * 3 + 2], nl = Math.hypot(nx, ny, nz) || 1;
      nx /= nl; ny /= nl; nz /= nl;
      var back = nx * vdir[0] + ny * vdir[1] + nz * vdir[2] < 0;
      if (back) { nx = -nx; ny = -ny; nz = -nz; }
      var fill = shadeCol(base, nx, ny, nz, back);
      ctx.beginPath(); ctx.moveTo(scr[a][0], scr[a][1]); ctx.lineTo(scr[b][0], scr[b][1]); ctx.lineTo(scr[q][0], scr[q][1]); ctx.closePath();
      ctx.fillStyle = fill; ctx.strokeStyle = fill; ctx.lineWidth = 0.8; ctx.fill(); ctx.stroke();
    }
    // the elastic hem where it shows
    ctx.strokeStyle = rgb(mix(base, [0, 0, 0], 0.32)); ctx.lineWidth = Math.max(1.5, v.s * 0.24); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    var hem = c.hem, on = false;
    ctx.beginPath();
    for (i = 0; i < hem.length; i++) {
      var h = hem[i], hx = P[h * 3], hy = P[h * 3 + 1], hz = P[h * 3 + 2];
      var hidden = hy < -0.05 && hx < 0.05 && hz < 0.05;
      if (hidden) { on = false; continue; }
      if (on) ctx.lineTo(scr[h][0], scr[h][1]); else { ctx.moveTo(scr[h][0], scr[h][1]); on = true; }
    }
    ctx.stroke();
    ctx.restore();
    // dimension line: mattress height, with the pocket marked on it
    var off = 7, d0 = pr(off, 0, -2), d1 = pr(off, H, -2);
    var tick = v.s * 1.1;
    ctx.strokeStyle = pal.red; ctx.fillStyle = pal.red; ctx.lineWidth = Math.max(1, v.s * 0.12);
    ctx.beginPath(); ctx.moveTo(d0[0], d0[1]); ctx.lineTo(d1[0], d1[1]);
    ctx.moveTo(d0[0] - tick, d0[1]); ctx.lineTo(d0[0] + tick, d0[1]); ctx.moveTo(d1[0] - tick, d1[1]); ctx.lineTo(d1[0] + tick, d1[1]); ctx.stroke();
    ctx.setLineDash([v.s * 0.5, v.s * 0.4]); ctx.globalAlpha = 0.6;
    var e0 = pr(0.8, 0, -2), e1 = pr(0.8, H, -2);
    ctx.beginPath(); ctx.moveTo(d0[0], d0[1]); ctx.lineTo(e0[0], e0[1]); ctx.moveTo(d1[0], d1[1]); ctx.lineTo(e1[0], e1[1]); ctx.stroke();
    ctx.setLineDash([]); ctx.globalAlpha = 1;
    var fs2 = Math.round(Math.max(11, v.s * 1.55));
    ctx.font = '600 ' + fs2 + 'px Manrope, Inter, system-ui, sans-serif'; ctx.textBaseline = 'middle';
    var mid = pr(off, H / 2, -2);
    ctx.fillText(labels.height, mid[0] + tick * 1.6, mid[1]);
  };

  /* ---------------- mount ---------------- */
  function mount(canvas, opts) {
    opts = opts || {};
    var painter = new Painter(canvas);
    painter.dark = !!opts.dark; painter.bg = opts.bg || painter.bg;
    var sheets = opts.sheets;
    var cur = opts.sheet || Object.keys(sheets)[0];
    var sim = new Sim(sheets[cur], opts.height || 12);
    var raf = 0, last = 0, acc = 0, visible = true, listeners = [];
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var label = opts.label || function (h, p) { return { height: h + ' in.', pocket: p + ' in. pocket' }; };
    function size() {
      var r = canvas.getBoundingClientRect(), dpr = Math.min(2, window.devicePixelRatio || 1);
      var w = Math.max(1, Math.round(r.width * dpr)), h = Math.max(1, Math.round(r.height * dpr));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    }
    function fmt(x) { return (Math.round(x * 2) / 2).toString(); }
    function paint() { painter.draw(sim, label(fmt(sim.Ht), fmt(sim.sheet.pocket))); }
    function emit() { var st = state(); listeners.forEach(function (f) { f(st); }); }
    function state() { return { sheet: cur, height: sim.Ht, fits: sim.fits(sim.Ht), pocket: sim.sheet.pocket, settled: sim.asleep() }; }
    function settle(maxSteps) { for (var k = 0; k < (maxSteps || 900); k++) { sim.step(1 / 120); if (sim.asleep()) break; } }
    function frame(now) {
      raf = 0;
      if (!visible) return;
      var dt = Math.min(0.05, (now - (last || now)) / 1000); last = now; acc += dt;
      var steps = 0;
      while (acc >= 1 / 120 && steps < 6) { sim.step(1 / 120); acc -= 1 / 120; steps++; }
      paint();
      if (sim.asleep()) { emit(); return; }
      raf = requestAnimationFrame(frame);
    }
    function wake() { if (reduce) { settle(); paint(); emit(); return; } if (!raf && visible) { last = 0; acc = 0; raf = requestAnimationFrame(frame); } }
    size();
    if (opts.startSettled !== false) { settle(1600); }
    paint();
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) { visible = es[0].isIntersecting; if (visible && !sim.asleep()) wake(); }, { rootMargin: '80px' }).observe(canvas);
    }
    if ('ResizeObserver' in window) new ResizeObserver(function () { size(); paint(); }).observe(canvas);
    // pull the corner: grab the nearest piece of cloth and drag it
    function toLocal(e) { var r = canvas.getBoundingClientRect(), dpr = canvas.width / Math.max(1, r.width); return [(e.clientX - r.left) * dpr, (e.clientY - r.top) * dpr]; }
    canvas.addEventListener('pointerdown', function (e) {
      var m = toLocal(e), v = painter.view(canvas.width, canvas.height), best = -1, bd = 1e9;
      for (var i = 0; i < sim.c.n; i++) {
        if (sim.c.kind[i] === 0 && !(sim.c.fx[i] > -6 && sim.c.fz[i] > -6)) continue;
        var s = project(sim.p[i * 3], sim.p[i * 3 + 1], sim.p[i * 3 + 2], v), d = Math.hypot(s[0] - m[0], s[1] - m[1]);
        if (d < bd) { bd = d; best = i; }
      }
      if (best < 0 || bd > v.s * 6) return;
      sim.grab = best; sim.grabTo = [sim.p[best * 3], sim.p[best * 3 + 1], sim.p[best * 3 + 2]]; sim.calm = 0;
      canvas.setPointerCapture(e.pointerId); canvas.classList.add('is-grab'); e.preventDefault(); wake();
      var start = m, from = sim.grabTo.slice();
      function move(ev) {
        var q = toLocal(ev), f = 1 / v.s, dx = (q[0] - start[0]) * f, dy = (q[1] - start[1]) * f;
        sim.grabTo = [from[0] + dx * RX - dy * UX, from[1] - dy * UY, from[2] + dx * RZ - dy * UZ];
        sim.calm = 0; wake();
      }
      function up() { sim.grab = -1; sim.grabTo = null; canvas.classList.remove('is-grab'); canvas.removeEventListener('pointermove', move); canvas.removeEventListener('pointerup', up); canvas.removeEventListener('pointercancel', up); wake(); }
      canvas.addEventListener('pointermove', move); canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);
    });
    return {
      setHeight: function (h) {
        var was = sim.fits(sim.Ht); sim.Ht = h; sim.calm = 0;
        // back to a height the pocket covers after it came off: put the sheet on again
        if (!was && sim.fits(h)) { sim.H = h; sim.dress(true, 2.5); }
        emit(); wake();
      },
      setSheet: function (id) {
        if (!sheets[id] || id === cur) return; cur = id;
        var h = sim.Ht; sim = new Sim(sheets[id], h); if (sim.fits(h)) sim.dress(true, 2.5); else sim.dress(true, 0); emit(); wake();
      },
      setColor: function (hexc, id) { var sh = sheets[id || cur]; if (sh) sh.color = hexc; paint(); },
      setDark: function (d, bg) { painter.dark = !!d; if (bg) painter.bg = bg; paint(); },
      snap: function () { sim.dress(true, 1.8); wake(); },
      settle: function () { settle(2400); paint(); emit(); },
      state: state,
      on: function (f) { listeners.push(f); },
      wake: wake
    };
  }

  if (typeof window !== 'undefined') window.MOBdPocket = { mount: mount, TUCK: TUCK };
  if (typeof module !== 'undefined') module.exports = { Sim: Sim, buildCloth: buildCloth };
})();
